import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, Stethoscope } from 'lucide-react';
import { NAV, CLINIC } from '../mock';
import { useLang, LangToggle } from '../context/LangContext';

const PRIMARY = NAV.slice(0, 6);
const MORE = NAV.slice(6);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const loc = useLocation();
  const { tr } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMoreOpen(false); }, [loc.pathname]);

  const isActive = (to) => (to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50">
      {/* Top bar */}
      <div className="deep-section text-teal-50 text-sm hidden md:block">
        <div className="container-x flex items-center justify-between py-2">
          <span className="opacity-90">{tr('Rheumatology & Autoimmune Disease Specialists — Noida / Delhi NCR', 'रूमेटोलॉजी और ऑटोइम्यून रोग विशेषज्ञ — नोएडा / दिल्ली NCR')}</span>
          <a href={`tel:${CLINIC.phoneRaw}`} className="flex items-center gap-2 font-medium hover:text-white transition-colors">
            <Phone size={15} /> {tr('For appointments', 'अपॉइंटमेंट के लिए')}: {CLINIC.phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className={`transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur shadow-[0_8px_30px_-12px_rgba(11,59,58,0.18)]' : 'bg-white'}`}>
        <div className="container-x flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-xl text-white" style={{ background: 'linear-gradient(135deg,#0f766e,#0b3b3a)' }}>
              <Stethoscope size={22} />
            </span>
            <span className="leading-tight">
              <span className="block font-display text-[19px] font-600 text-teal-900" style={{ fontWeight: 600 }}>Srigyan</span>
              <span className="block text-[11px] tracking-wide text-teal-700 uppercase whitespace-nowrap">Rheumatology Clinic</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-4 2xl:gap-6">
            {PRIMARY.map((n) => (
              <Link key={n.to} to={n.to} className={`text-[14px] xl:text-[15px] whitespace-nowrap link-underline ${isActive(n.to) ? 'text-teal-700 font-semibold' : 'text-slate-700 hover:text-teal-700'}`}>
                {tr(n.label, n.hi)}
              </Link>
            ))}
            <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
              <button className="flex items-center gap-1 text-[14px] xl:text-[15px] whitespace-nowrap text-slate-700 hover:text-teal-700">
                {tr('More', 'और')} <ChevronDown size={15} className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full pt-3">
                  <div className="w-60 bg-white rounded-2xl shadow-xl border border-teal-50 p-2">
                    {MORE.map((n) => (
                      <Link key={n.to} to={n.to} className="block px-4 py-2.5 rounded-xl text-[15px] text-slate-700 hover:bg-teal-50 hover:text-teal-800 transition-colors">
                        {tr(n.label, n.hi)}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <LangToggle />
            <a href={`tel:${CLINIC.phoneRaw}`} className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap btn-coral px-5 py-2.5 rounded-full font-semibold text-sm">
              <Phone size={16} /> <span className="xl:hidden 2xl:inline">{tr('Call Now', 'कॉल करें')}</span>
            </a>
            <button data-testid="mobile-menu-toggle" className="xl:hidden grid place-items-center w-11 h-11 rounded-xl bg-teal-50 text-teal-800" onClick={() => setOpen(true)} aria-label="Open menu">
              <Menu size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-[60] xl:hidden transition ${open ? 'visible' : 'invisible'}`}>
        <div className={`absolute inset-0 bg-teal-900/40 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`} onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-white shadow-2xl transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between p-5 border-b border-teal-50">
            <span className="font-display text-lg text-teal-900">Menu</span>
            <button onClick={() => setOpen(false)} className="grid place-items-center w-10 h-10 rounded-lg bg-teal-50 text-teal-800"><X size={20} /></button>
          </div>
          <nav className="p-3 overflow-y-auto h-[calc(100%-140px)]">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className={`block px-4 py-3 rounded-xl text-[15px] ${isActive(n.to) ? 'bg-teal-50 text-teal-800 font-semibold' : 'text-slate-700'}`}>
                {tr(n.label, n.hi)}
              </Link>
            ))}
          </nav>
          <div className="p-5 border-t border-teal-50">
            <a href={`tel:${CLINIC.phoneRaw}`} className="flex items-center justify-center gap-2 btn-coral w-full py-3 rounded-full font-semibold">
              <Phone size={17} /> {CLINIC.phone}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
